import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { dateSchema, idSchema, objectIdSchema } from '../../validation';

/**
 * Lifecycle of a fan-out task — see `docs/ai/fan-out-tasks.md`.
 *
 * - `queued`             — created, awaiting worker pickup.
 * - `in_progress`        — worker has claimed this task and is
 *                          executing.
 * - `waiting_response`   — task dispatched an external action (push /
 *                          WhatsApp / call) and is waiting on the
 *                          counterparty.
 * - `completed`          — terminal success.
 * - `failed`             — terminal failure (max attempts exhausted).
 * - `timed_out`          — `deadlineAt` passed without a response.
 * - `cancelled`          — user or system cancelled the parent.
 *
 * A parent task's status is derived from its children — see
 * `docs/ai/fan-out-tasks.md` "Lifecycle states".
 */
export const agentTaskStatusSchema = z.enum([
  'queued',
  'in_progress',
  'waiting_response',
  'completed',
  'failed',
  'timed_out',
  'cancelled',
]);
export type AgentTaskStatus = z.infer<typeof agentTaskStatusSchema>;

/**
 * Cross-refs to entities a task is acting on. Same shape as
 * {@link agentActivityRefsSchema} for symmetry.
 */
export const agentTaskRefsSchema = z.object({
  orderId: objectIdSchema.optional(),
  driverId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  contactId: objectIdSchema.optional(),
  vehicleId: objectIdSchema.optional(),
  savedLocationId: objectIdSchema.optional(),
});
export type AgentTaskRefs = z.infer<typeof agentTaskRefsSchema>;

/**
 * `AgentTask` — durable post-chat work.
 *
 * MVP ships the schema and CRUD service; the worker / executor lands
 * Phase 2 with the first `allocate_drivers` flow. The
 * `AgentTaskExecutor` interface (see `docs/ai/fan-out-tasks.md`)
 * isolates the implementation so swapping to Inngest / BullMQ later
 * is a contained change.
 */
export const AgentTaskSchema = createModel({
  /**
   * Owning tenant — real Org only, absent when the origin
   * conversation has no org (same contract as
   * `ConversationSchema.org`).
   */
  org: partySchema.optional(),
  conversationId: objectIdSchema,
  parentTaskId: objectIdSchema.optional(),

  /**
   * Open-ended type — tool authors register new types when they
   * introduce a new fan-out flow. See
   * `docs/ai/fan-out-tasks.md`.
   *
   * Known MVP-time types (more to come):
   * - `allocate_drivers` (parent)
   * - `allocate_driver_child`
   * - `dormant_driver_checkin`
   * - `bulk_invoice_reminder`
   */
  type: idSchema.min(1),

  status: agentTaskStatusSchema.default('queued'),

  refs: agentTaskRefsSchema.optional(),

  /** Type-specific input — opaque to the framework. */
  payload: z.record(z.unknown()).optional(),
  /** Type-specific output — opaque to the framework. */
  result: z.record(z.unknown()).optional(),

  currentStep: z.number().int().nonnegative().optional(),
  totalSteps: z.number().int().positive().optional(),

  attempts: z.number().int().nonnegative().default(0),
  maxAttempts: z.number().int().positive().default(3),

  scheduledFor: dateSchema.optional(),
  deadlineAt: dateSchema.optional(),
  startedAt: dateSchema.optional(),
  completedAt: dateSchema.optional(),
  nextAttemptAt: dateSchema.optional(),

  errorMessage: z.string().optional(),
});

export type AgentTask = z.infer<typeof AgentTaskSchema.model>;
export type AgentTaskData = z.infer<typeof AgentTaskSchema.data>;
export type AgentTaskPartial = z.infer<typeof AgentTaskSchema.partial>;
export type AgentTaskQueryOne = QueryOne<AgentTask>;
export type AgentTaskQueryMany = QueryMany<AgentTask>;
